import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DateSelector from './shared/DateSelector';
import { zoomLevels } from '../../utils/settings';
import ColorPicker from '../ColorPicker';

const styles = {
  fileInput: {
    display: 'none'
  },
  browseButton: {
    marginLeft: 1
  },
  timelineItemBox: {
    p: 2,
    mb: 2,
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    position: 'relative'
  }
};

const SettingsPanel = ({ settings, onSettingsChange }) => {
  const [settingsForm, setSettingsForm] = useState({ ...settings });

  // File input refs
  const backgroundImageInputRef = useRef(null);
  const logoUrlInputRef = useRef(null);
  const coverImageInputRef = useRef(null);

  // Update settings form when settings prop changes
  useEffect(() => {
    setSettingsForm({ ...settings });
  }, [settings]);

  // Generic form change handler
  const handleSettingsChange = (field) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    // Validate timeSkip to ensure it's always at least 1
    if (field === 'timeSkip') {
      const timeSkipValue = parseInt(value, 10);
      if (isNaN(timeSkipValue) || timeSkipValue < 1) {
        // Set to 1 if invalid or less than 1
        setSettingsForm(prev => ({
          ...prev,
          [field]: 1
        }));
        return;
      }
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setSettingsForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setSettingsForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle date changes
  const handleDateChange = (dateType) => (e) => {
    const { name, value } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    const newValue = isCheckbox ? e.target.checked : parseInt(value, 10);

    setSettingsForm(prev => ({
      ...prev,
      [dateType]: {
        ...prev[dateType],
        [name]: newValue
      }
    }));
  };

  // File input handlers
  const handleBrowseClick = (inputRef) => {
    inputRef.current.click();
  };

  const handleFileSelected = (field) => (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        setSettingsForm(prev => ({
          ...prev,
          [field]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle age line changes
  const handleAgeLineChange = (lineIndex, field) => (event) => {
    const value = event.target.value;

    setSettingsForm(prev => {
      const updatedAgeLines = [...(prev.ageLines || [])];

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedAgeLines[lineIndex] = {
          ...updatedAgeLines[lineIndex],
          [parent]: {
            ...updatedAgeLines[lineIndex][parent],
            [child]: child === 'isBC' ? event.target.checked : parseInt(value, 10)
          }
        };
      } else {
        updatedAgeLines[lineIndex] = {
          ...updatedAgeLines[lineIndex],
          [field]: value
        };
      }

      return {
        ...prev,
        ageLines: updatedAgeLines
      };
    });
  };

  // Handle shaded region changes
  const handleShadedRegionChange = (regionIndex, field) => (event) => {
    let value = event.target.value;
    if (field === 'opacity') {
      value = parseFloat(value);
    }

    setSettingsForm(prev => {
      const updatedRegions = [...(prev.shadedRegions || [])];

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedRegions[regionIndex] = {
          ...updatedRegions[regionIndex],
          [parent]: {
            ...updatedRegions[regionIndex][parent],
            [child]: child === 'isBC' ? event.target.checked : parseInt(value, 10)
          }
        };
      } else {
        updatedRegions[regionIndex] = {
          ...updatedRegions[regionIndex],
          [field]: value
        };
      }

      return {
        ...prev,
        shadedRegions: updatedRegions
      };
    });
  };

  // Handle opacity change from slider
  const handleOpacityChange = (regionIndex, newValue) => {
    setSettingsForm(prev => {
      const updatedRegions = [...(prev.shadedRegions || [])];
      updatedRegions[regionIndex] = {
        ...updatedRegions[regionIndex],
        opacity: newValue
      };

      return {
        ...prev,
        shadedRegions: updatedRegions
      };
    });
  };

  // Add new age line
  const addAgeLine = () => {
    const newAgeLine = {
      name: '',
      startDate: {
        isBC: false
      },
      endDate: {
        isBC: false
      },
      color: '#3b82f6'  // Default to blue color
    };

    setSettingsForm(prev => ({
      ...prev,
      ageLines: [...(prev.ageLines || []), newAgeLine]
    }));
  };

  // Add new shaded region
  const addShadedRegion = () => {
    const newShadedRegion = {
      startDate: {
        isBC: false
      },
      endDate: {
        isBC: false
      },
      color: '#f87171',  // Default to red color
      opacity: 0.3
    };

    setSettingsForm(prev => ({
      ...prev,
      shadedRegions: [...(prev.shadedRegions || []), newShadedRegion]
    }));
  };

  // Remove age line
  const removeAgeLine = (lineIndex) => {
    setSettingsForm(prev => ({
      ...prev,
      ageLines: prev.ageLines.filter((_, i) => i !== lineIndex)
    }));
  };

  // Remove shaded region
  const removeShadedRegion = (regionIndex) => {
    setSettingsForm(prev => ({
      ...prev,
      shadedRegions: prev.shadedRegions.filter((_, i) => i !== regionIndex)
    }));
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      // Validate required dates in age lines and shaded regions
      const hasInvalidAgeLine = settingsForm.ageLines?.some(
        line => !line.startDate?.year || !line.endDate?.year
      );

      const hasInvalidShadedRegion = settingsForm.shadedRegions?.some(
        region => !region.startDate?.year || !region.endDate?.year
      );

      if (hasInvalidAgeLine || hasInvalidShadedRegion) {
        alert('Lütfen tüm dönem çizgileri ve taralı bölgeler için başlangıç ve bitiş tarihlerini giriniz.');
        return;
      }

      // Make sure we're properly formatting background settings
      const updatedSettings = {
        ...settingsForm,
        // Ensure backgroundColor is always defined even if backgroundImage is present
        backgroundColor: settingsForm.backgroundColor || '#ffffff',
        // Ensure ageLines and shadedRegions are arrays
        ageLines: settingsForm.ageLines || [],
        shadedRegions: settingsForm.shadedRegions || []
      };

      // Call the onSettingsChange prop with updated settings
      await onSettingsChange(updatedSettings);

      // Provide user feedback
      alert('Ayarlar başarıyla kaydedildi');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Ayarlar kaydedilirken bir hata oluştu');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Başlık"
          fullWidth
          margin="normal"
          value={settingsForm.title || ''}
          onChange={handleSettingsChange('title')}
        />
        <TextField
          label="Hakkında"
          fullWidth
          margin="normal"
          multiline
          rows={3}
          value={settingsForm.about || ''}
          onChange={handleSettingsChange('about')}
        />
        <TextField
          label="Zaman Atlama (Time Skip)"
          type="number"
          fullWidth
          margin="normal"
          value={settingsForm.timeSkip || 100}
          onChange={handleSettingsChange('timeSkip')}
          inputProps={{ min: 1 }}
          helperText="Zaman çizelgesinde atlama aralığı"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateSelector
          dateValue={settingsForm.startDate}
          onDateChange={handleDateChange('startDate')}
          label="Başlangıç Tarihi"
          required={true}
        />

        <DateSelector
          dateValue={settingsForm.endDate}
          onDateChange={handleDateChange('endDate')}
          label="Bitiş Tarihi"
          required={true}
        />

        <FormControl fullWidth margin="normal" disabled={!!settingsForm.manualStartDate && settingsForm.manualStartDate.year}>
          <InputLabel>Başlangıç Noktası</InputLabel>
          <Select
            value={settingsForm.beginAt || 'firstStory'}
            onChange={handleSettingsChange('beginAt')}
            label="Başlangıç Noktası"
          >
            <MenuItem value="firstStory">İlk Hikaye</MenuItem>
            <MenuItem value="lastStory">Son Hikaye</MenuItem>
            <MenuItem value="currentDate">Şu Anki Tarih</MenuItem>
          </Select>
          <Typography variant="caption" color="textSecondary">
            Eğer manuel başlangıç tarihi seçerseniz, bu alan devre dışı kalır ve sadece manuel tarih kullanılır.
          </Typography>
        </FormControl>
        {/* Görüntü Alanı (Zoom Seviyesi) Alanı */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Görüntü Alanı</InputLabel>
          <Select
            value={settingsForm.zoomLevel || zoomLevels[3].value}
            onChange={handleSettingsChange('zoomLevel')}
            label="Görüntü Alanı"
          >
            {zoomLevels.map((level, idx) => (
              <MenuItem key={level.value} value={level.value}>
                {level.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="textSecondary">
            Zaman çizelgesinin ilk açılışta hangi görüntü alanında başlayacağını seçin.
          </Typography>
        </FormControl>
        {/* Ekran Açılım Tarih Seçimi (Manuel Başlangıç Tarihi) Alanı */}
        <Box sx={{ mt: 2 }}>
          <DateSelector
            dateValue={settingsForm.manualStartDate}
            onDateChange={handleDateChange('manualStartDate')}
            label="Ekran Açılım Tarih Seçimi"
            disabled={!!settingsForm.beginAt && settingsForm.beginAt !== '' && settingsForm.beginAt !== undefined && settingsForm.beginAt !== null && settingsForm.beginAt !== 'firstStory'}
          />
          <Typography variant="caption" color="textSecondary">
            Eğer burada bir tarih seçerseniz, başlangıç noktası alanı devre dışı kalır ve sadece bu tarih kullanılır.
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={settingsForm.isPublic === undefined ? false : !!settingsForm.isPublic}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, isPublic: e.target.checked }))}
              name="isPublic"
            />
          }
          label="Zaman Çizelgesi Herkese Açık"
        />
        <Typography variant="caption" display="block" color="textSecondary" sx={{ pl: 4 }}>
          Herkese açık zaman çizelgeleri, ana sayfadaki zaman çizelgeleri listesinde görünecektir.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Arka Plan Rengi
            </Typography>
            <ColorPicker
              color={settingsForm.backgroundColor || '#FFFFFF'}
              onChange={(color) => handleSettingsChange('backgroundColor')({ target: { value: color } })}
            />
          </Box>
          <TextField
            label="Arka Plan Resmi"
            fullWidth
            value={settingsForm.backgroundImage || ''}
            onChange={handleSettingsChange('backgroundImage')}
            InputProps={{
              endAdornment: (
                <IconButton
                  edge="end"
                  onClick={() => handleBrowseClick(backgroundImageInputRef)}
                  sx={styles.browseButton}
                >
                  <ImageIcon />
                </IconButton>
              ),
            }}
          />
          <input
            type="file"
            ref={backgroundImageInputRef}
            style={styles.fileInput}
            accept="image/*"
            onChange={handleFileSelected('backgroundImage')}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Kapak Resmi"
            fullWidth
            value={settingsForm.coverImage || ''}
            onChange={handleSettingsChange('coverImage')}
            InputProps={{
              endAdornment: (
                <IconButton
                  edge="end"
                  onClick={() => handleBrowseClick(coverImageInputRef)}
                  sx={styles.browseButton}
                >
                  <ImageIcon />
                </IconButton>
              ),
            }}
          />
          <input
            type="file"
            ref={coverImageInputRef}
            style={styles.fileInput}
            accept="image/*"
            onChange={handleFileSelected('coverImage')}
          />
        </Box>
        <TextField
          label="Logo URL"
          fullWidth
          margin="normal"
          value={settingsForm.logoUrl || '/trbayrak.png'}
          onChange={handleSettingsChange('logoUrl')}
          InputProps={{
            endAdornment: (
              <IconButton
                edge="end"
                onClick={() => handleBrowseClick(logoUrlInputRef)}
                sx={styles.browseButton}
              >
                <ImageIcon />
              </IconButton>
            ),
          }}
        />
        <input
          type="file"
          ref={logoUrlInputRef}
          style={styles.fileInput}
          accept="image/*"
          onChange={handleFileSelected('logoUrl')}
        />
      </Grid>

      {/* NEW SECTION: Age Lines */}
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="age-lines-panel-content"
            id="age-lines-panel-header"
          >
            <Typography variant="subtitle1">Dönem Çizgileri</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                Belirli tarih aralıklarını çizgi ile işaretleyin
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addAgeLine}
                size="small"
                variant="outlined"
              >
                Dönem Çizgisi Ekle
              </Button>
            </Box>

            {/* Display age lines */}
            {settingsForm.ageLines?.length > 0 ? (
              settingsForm.ageLines.map((line, index) => (
                <Box
                  key={`age-line-${index}`}
                  sx={{
                    ...styles.timelineItemBox,
                    borderLeft: `4px solid ${line.color || '#3b82f6'}`,
                    border: (!line.startDate?.year || !line.endDate?.year) ?
                      '2px solid #f44336' : '1px solid #e0e0e0'
                  }}
                >
                  {(!line.startDate?.year || !line.endDate?.year) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Başlangıç ve bitiş tarihlerini girmelisiniz
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Typography variant="subtitle2">
                        Dönem Çizgisi {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeAgeLine(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="İsim"
                        fullWidth
                        value={line.name || ''}
                        onChange={handleAgeLineChange(index, 'name')}
                        placeholder="Dönem çizgisi ismi"
                        margin="dense"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateSelector
                        dateValue={line.startDate}
                        onDateChange={(e) => {
                          const { name, value, type, checked } = e.target;
                          const newValue = type === 'checkbox' ? checked : parseInt(value, 10);

                          setSettingsForm(prev => {
                            const updatedAgeLines = [...(prev.ageLines || [])];
                            updatedAgeLines[index] = {
                              ...updatedAgeLines[index],
                              startDate: {
                                ...updatedAgeLines[index].startDate,
                                [name]: newValue
                              }
                            };

                            return {
                              ...prev,
                              ageLines: updatedAgeLines
                            };
                          });
                        }}
                        label="Başlangıç Tarihi"
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DateSelector
                        dateValue={line.endDate}
                        onDateChange={(e) => {
                          const { name, value, type, checked } = e.target;
                          const newValue = type === 'checkbox' ? checked : parseInt(value, 10);

                          setSettingsForm(prev => {
                            const updatedAgeLines = [...(prev.ageLines || [])];
                            updatedAgeLines[index] = {
                              ...updatedAgeLines[index],
                              endDate: {
                                ...updatedAgeLines[index].endDate,
                                [name]: newValue
                              }
                            };

                            return {
                              ...prev,
                              ageLines: updatedAgeLines
                            };
                          });
                        }}
                        label="Bitiş Tarihi"
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                          Renk
                        </Typography>
                        <ColorPicker
                          color={line.color || '#3b82f6'}
                          onChange={(color) => handleAgeLineChange(index, 'color')({ target: { value: color } })}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                Henüz dönem çizgisi eklenmemiş
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* NEW SECTION: Shaded Regions */}
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="shaded-regions-panel-content"
            id="shaded-regions-panel-header"
          >
            <Typography variant="subtitle1">Taralı Bölgeler</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                Zaman çizelgesi üzerinde belirli tarih aralıklarını renk ile vurgulayın
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addShadedRegion}
                size="small"
                variant="outlined"
              >
                Taralı Bölge Ekle
              </Button>
            </Box>

            {/* Display shaded regions */}
            {settingsForm.shadedRegions?.length > 0 ? (
              settingsForm.shadedRegions.map((region, index) => {
                const backgroundColor = region.color || '#f87171';
                const opacity = region.opacity || 0.3;

                return (
                  <Box
                    key={`shaded-region-${index}`}
                    sx={{
                      ...styles.timelineItemBox,
                      borderLeft: '4px solid',
                      borderLeftColor: backgroundColor,
                      backgroundColor: 'white'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 1,
                              borderRadius: '4px',
                              backgroundColor: backgroundColor,
                              opacity: opacity,
                              border: '1px solid #ccc'
                            }}
                          />
                          <Typography variant="subtitle2">
                            Taralı Bölge {index + 1}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => removeShadedRegion(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <DateSelector
                          dateValue={region.startDate}
                          onDateChange={(e) => {
                            const { name, value, type, checked } = e.target;
                            const newValue = type === 'checkbox' ? checked : parseInt(value, 10);

                            setSettingsForm(prev => {
                              const updatedRegions = [...(prev.shadedRegions || [])];
                              updatedRegions[index] = {
                                ...updatedRegions[index],
                                startDate: {
                                  ...updatedRegions[index].startDate,
                                  [name]: newValue
                                }
                              };

                              return {
                                ...prev,
                                shadedRegions: updatedRegions
                              };
                            });
                          }}
                          label="Başlangıç Tarihi"
                          required={true}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DateSelector
                          dateValue={region.endDate}
                          onDateChange={(e) => {
                            const { name, value, type, checked } = e.target;
                            const newValue = type === 'checkbox' ? checked : parseInt(value, 10);

                            setSettingsForm(prev => {
                              const updatedRegions = [...(prev.shadedRegions || [])];
                              updatedRegions[index] = {
                                ...updatedRegions[index],
                                endDate: {
                                  ...updatedRegions[index].endDate,
                                  [name]: newValue
                                }
                              };

                              return {
                                ...prev,
                                shadedRegions: updatedRegions
                              };
                            });
                          }}
                          label="Bitiş Tarihi"
                          required={true}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                            Renk
                          </Typography>
                          <ColorPicker
                            color={region.color || '#f87171'}
                            onChange={(color) => handleShadedRegionChange(index, 'color')({ target: { value: color } })}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography gutterBottom>
                          Opaklık: {region.opacity || 0.3}
                        </Typography>
                        <Slider
                          value={region.opacity || 0.3}
                          onChange={(e, newValue) => handleOpacityChange(index, newValue)}
                          min={0}
                          max={1}
                          step={0.1}
                          valueLabelDisplay="auto"
                        />

                        <Box
                          sx={{
                            mt: 2,
                            height: '24px',
                            width: '100%',
                            backgroundColor: backgroundColor,
                            opacity: opacity,
                            borderRadius: '4px',
                            border: '1px solid #ccc'
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">
                Henüz taralı bölge eklenmemiş
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
          >
            Ayarları Kaydet
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SettingsPanel;